// ==========================================================================
// TerminPipelineTabelle — Termin-Uebersicht mit Pipeline-Status je Termin
// ==========================================================================
// Zeitraum ist ueber eine "von KW – bis KW"-Auswahl oben frei einstellbar.
// Standard: vorige + aktuelle + kommende Kalenderwoche.
//
// Layout:
//   - Spalten Kunde/Termin/Art sind schmal gehalten (Hendrik-Feedback).
//   - 7 Status-Spalten rechts mit Icons im Kopf (statt 1..7).
//   - Klick auf den Namen -> oeffnet die Kunden-Akte in einem NEUEN Tab
//     (Deeplink ?kunde=<id>, ausgewertet in App.jsx).
//   - Klick auf Punkt 2/4/7 -> Toggle/Cycle (manuell).
// ==========================================================================

import React, { useMemo, useState, useEffect } from 'react';
import { api, ApiError } from '../api';
import TerminPipelinePunkte, { SPALTEN_ICONS } from './TerminPipelinePunkte';

// --- Datums-/KW-Helfer (lokale Zeit, kein UTC-Versatz) --------------------
function mondayOf(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; // 0 = Montag
  x.setDate(x.getDate() - day);
  return x;
}
function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function isoOf(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function ddmm(d) {
  return String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.';
}

function kalenderwoche(isoDatum) {
  if (!isoDatum) return null;
  const d = new Date(isoDatum + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target - firstThursday;
  const woche = 1 + Math.round(diff / (7 * 24 * 3600 * 1000));
  return { jahr: target.getFullYear(), woche };
}

function formatTerminDatum(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

function istAbgeschlossen(p) {
  if (!p) return false;
  // Ein Termin ist erst dann wirklich "abgeschlossen", wenn die Abrechnung
  // eingegangen ist (Spalte 7, manuell abgehakt). Alles davor — auch schon
  // "in AS-Bremen-Warteliste" oder "an AS Bremen versendet" — ist noch ein
  // OFFENER Vorgang (Hendrik wartet auf die Bezahlung). Deshalb duerfen
  // solche Termine NICHT vom "Nur offene"-Filter ausgeblendet werden.
  return !!p.spalte_7_done;
}

function istInZukunft(isoDatum) {
  if (!isoDatum) return false;
  const heute = new Date(); heute.setHours(0, 0, 0, 0);
  const d = new Date(isoDatum + 'T00:00:00');
  return d >= heute;
}

export default function TerminPipelineTabelle({ onKundeWaehlen, refreshKey }) {
  const [termine, setTermine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nurOffene, setNurOffene] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // KW-Auswahl als Offsets relativ zur aktuellen Kalenderwoche.
  // Standard: -1 (vorige KW) bis +1 (kommende KW).
  const [vonOff, setVonOff] = useState(-1);
  const [bisOff, setBisOff] = useState(1);

  // Wochen-Optionen einmal pro Session aufbauen (heute ist fix genug).
  const wochen = useMemo(() => {
    const heuteMonday = mondayOf(new Date());
    const list = [];
    for (let off = -26; off <= 26; off++) {
      const mon = addDays(heuteMonday, off * 7);
      const son = addDays(mon, 6);
      const kw = kalenderwoche(isoOf(mon));
      list.push({
        off,
        monIso: isoOf(mon),
        sonIso: isoOf(son),
        label: `KW ${kw ? kw.woche : '?'} · ${ddmm(mon)}–${ddmm(son)}${off === 0 ? ' (aktuell)' : ''}`,
      });
    }
    return list;
  }, []);

  const vonWoche = wochen.find(w => w.off === vonOff) || wochen.find(w => w.off === 0);
  const bisWoche = wochen.find(w => w.off === bisOff) || wochen.find(w => w.off === 0);

  useEffect(() => {
    let abgebrochen = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const von = vonWoche ? vonWoche.monIso : isoOf(mondayOf(new Date()));
        const bis = bisWoche ? bisWoche.sonIso : isoOf(addDays(mondayOf(new Date()), 6));
        const liste = await api.get(`/api/termine?von=${von}&bis=${bis}`);
        if (!abgebrochen) setTermine(Array.isArray(liste) ? liste : []);
      } catch (err) {
        if (!abgebrochen) {
          const msg = err instanceof ApiError ? (err.detail || 'Fehler beim Laden.') : 'Verbindung zum Server fehlgeschlagen.';
          setError(msg);
        }
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    })();
    return () => { abgebrochen = true; };
  }, [vonOff, bisOff, reloadKey, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleVonChange(e) {
    const v = parseInt(e.target.value, 10);
    setVonOff(v);
    if (v > bisOff) setBisOff(v);
  }
  function handleBisChange(e) {
    const v = parseInt(e.target.value, 10);
    setBisOff(v);
    if (v < vonOff) setVonOff(v);
  }
  function standardZuruecksetzen() {
    setVonOff(-1);
    setBisOff(1);
  }

  async function handleToggleStattgefunden(terminId) {
    try {
      await api.post(`/api/termine/${terminId}/stattgefunden`, {});
      setReloadKey(k => k + 1);
    } catch (err) {
      console.warn('Pipeline: stattgefunden-Toggle fehlgeschlagen', err);
      alert('Konnte nicht abhaken — bitte spaeter erneut probieren.');
    }
  }
  async function handleToggleBegleitMail(terminId) {
    try {
      await api.post(`/api/termine/${terminId}/begleit-mail-status`, {});
      setReloadKey(k => k + 1);
    } catch (err) {
      console.warn('Pipeline: begleit-mail-Toggle fehlgeschlagen', err);
      alert('Konnte nicht umschalten — bitte spaeter erneut probieren.');
    }
  }
  async function handleToggleAbrechnungErledigt(terminId) {
    try {
      await api.post(`/api/termine/${terminId}/abrechnung-erledigt`, {});
      setReloadKey(k => k + 1);
    } catch (err) {
      console.warn('Pipeline: abrechnung-Toggle fehlgeschlagen', err);
      alert('Konnte nicht umschalten — bitte spaeter erneut probieren.');
    }
  }

  // Gruppierung nach KW. Zukunfts-KWs zuerst (oben), dann vergangene rueckwaerts.
  const gruppen = useMemo(() => {
    const gefiltert = nurOffene ? termine.filter(t => !istAbgeschlossen(t.pipeline)) : termine;
    const map = new Map();
    for (const t of gefiltert) {
      const kw = kalenderwoche(t.date);
      const key = kw ? `${kw.jahr}-KW${kw.woche.toString().padStart(2, '0')}` : 'ohne-datum';
      if (!map.has(key)) {
        map.set(key, { key, kw, eintraege: [], zukunft: false });
      }
      map.get(key).eintraege.push(t);
      if (istInZukunft(t.date)) map.get(key).zukunft = true;
    }
    // Sortierung: KWs absteigend (neueste/zukuenftige oben)
    const result = Array.from(map.values());
    result.sort((a, b) => (b.key > a.key ? 1 : b.key < a.key ? -1 : 0));
    for (const g of result) {
      g.eintraege.sort((a, b) => {
        if (a.date !== b.date) return (a.date || '').localeCompare(b.date || '');
        return (a.time || '').localeCompare(b.time || '');
      });
    }
    return result;
  }, [termine, nurOffene]);

  const istStandard = vonOff === -1 && bisOff === 1;

  return (
    <div style={{ padding: '0 16px 24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#111827', margin: 0 }}>
          Termin-Pipeline
        </h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#374151', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={nurOffene}
            onChange={e => setNurOffene(e.target.checked)}
            style={{ width: 16, height: 16 }}
          />
          Nur offene Termine zeigen
        </label>
      </div>

      {/* KW-Bereichsauswahl */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        marginBottom: 14, padding: '10px 12px',
        background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Zeitraum:</span>
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
          Von
          <select value={vonOff} onChange={handleVonChange} style={selectStyle}>
            {wochen.map(w => (
              <option key={w.off} value={w.off}>{w.label}</option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 13, color: '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
          Bis
          <select value={bisOff} onChange={handleBisChange} style={selectStyle}>
            {wochen.map(w => (
              <option key={w.off} value={w.off}>{w.label}</option>
            ))}
          </select>
        </label>
        {!istStandard && (
          <button
            type="button"
            onClick={standardZuruecksetzen}
            style={{
              fontSize: 12, color: '#7c3aed', background: 'none', border: 'none',
              cursor: 'pointer', textDecoration: 'underline', padding: '2px 4px',
            }}
          >
            Standard (vorige–kommende KW)
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: 24, color: '#6b7280' }}>Pipeline wird geladen…</div>
      ) : error ? (
        <div style={{ padding: 24, color: '#b91c1c' }}>{error}</div>
      ) : termine.length === 0 ? (
        <div style={{ padding: 24, color: '#6b7280' }}>
          Keine Termine im gewaehlten Zeitraum.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '24%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '54%' }} />
            </colgroup>
            <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <tr>
                <th style={th}>Kunde</th>
                <th style={th}>Termin</th>
                <th style={th}>Art</th>
                <th style={{ ...th, paddingLeft: 8 }}>
                  <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                    {SPALTEN_ICONS.map((s, i) => {
                      const Icon = s.icon;
                      return (
                        <span
                          key={i}
                          title={s.label}
                          style={{
                            position: 'relative',
                            width: 28, height: 28,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            color: '#374151',
                          }}
                        >
                          <Icon size={14} strokeWidth={2.25} />
                          {s.asBadge && (
                            <span style={{
                              position: 'absolute', top: -2, right: -2,
                              background: '#111827', color: '#fff',
                              fontSize: 8, fontWeight: 700, lineHeight: 1,
                              padding: '2px 3px', borderRadius: 3,
                            }}>AS</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {gruppen.map(g => (
                <React.Fragment key={g.key}>
                  <tr>
                    <td colSpan={4} style={{
                      background: g.zukunft ? '#fef3c7' : '#eef2ff',
                      padding: '8px 12px', fontWeight: 600,
                      color: g.zukunft ? '#92400e' : '#3730a3',
                      borderTop: '1px solid #e5e7eb',
                    }}>
                      {g.zukunft ? '↑ Kommend — ' : ''}
                      {g.kw ? `KW ${g.kw.woche} / ${g.kw.jahr}` : 'Ohne Datum'}
                      {` — ${g.eintraege.length} Termin${g.eintraege.length === 1 ? '' : 'e'}`}
                    </td>
                  </tr>
                  {g.eintraege.map(t => (
                    <tr key={t.id} style={{ borderTop: '1px solid #f3f4f6' }}>
                      <td style={td}>
                        {t.kundeId ? (
                          <a
                            href={`?kunde=${encodeURIComponent(t.kundeId)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Akte in neuem Tab oeffnen"
                            style={{
                              color: '#7c3aed', textDecoration: 'underline',
                              fontWeight: 500, fontSize: 14, cursor: 'pointer',
                            }}
                          >
                            {(t.firstName || '') + ' ' + (t.lastName || '')}
                          </a>
                        ) : (
                          <span style={{ color: '#1f2937' }}>
                            {(t.firstName || '') + ' ' + (t.lastName || '')}
                          </span>
                        )}
                      </td>
                      <td style={td}>
                        {formatTerminDatum(t.date)} {t.time ? `· ${t.time}` : ''}
                      </td>
                      <td style={td}>
                        <span style={{
                          display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 11,
                          background: t.type === 'video' ? '#dbeafe' : '#dcfce7',
                          color: t.type === 'video' ? '#1e40af' : '#166534',
                        }}>
                          {t.type === 'video' ? 'Video' : 'Vor Ort'}
                        </span>
                      </td>
                      <td style={{ ...td, paddingLeft: 8 }}>
                        <TerminPipelinePunkte
                          pipeline={t.pipeline}
                          kompakt
                          onToggleStattgefunden={() => handleToggleStattgefunden(t.id)}
                          onToggleBegleitMail={() => handleToggleBegleitMail(t.id)}
                          onToggleAbrechnungErledigt={() => handleToggleAbrechnungErledigt(t.id)}
                        />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 12 }}>
        Klick auf den Namen oeffnet die Akte in einem neuen Tab. Punkt 2 (Termin
        stattgefunden), 4 (Begleit-Mail) und 7 (Abrechnung eingegangen) sind
        klickbar. Punkt 4 hat drei Zustaende: grau (nichts geplant) → rot
        (geplant, muss noch geschrieben werden) → gruen (erledigt) → grau. Alle
        anderen Punkte werden automatisch gruen, sobald der Schritt erledigt ist.
      </p>
    </div>
  );
}

const th = {
  textAlign: 'left', padding: '10px 12px', fontWeight: 600,
  color: '#374151', fontSize: 13, borderBottom: '1px solid #e5e7eb',
};
const td = { padding: '10px 12px', color: '#1f2937', verticalAlign: 'middle' };
const selectStyle = {
  fontSize: 13, padding: '4px 8px', borderRadius: 6,
  border: '1px solid #d1d5db', background: '#fff', color: '#111827', cursor: 'pointer',
};
